import type {
  AdBriefAssetsBoolExp,
  AdBriefsBoolExp,
  AdStudioProjectsBoolExp,
} from "src/graphql";

// Draft ideation rows belong in the Ideas feed — exclude from user-facing
// project dashboards unless promoted.
export const USER_FACING_AD_STUDIO_PROJECT_FILTER: AdStudioProjectsBoolExp = {
  _or: [{ source: { _neq: "ideation" } }, { status: { _eq: "promoted" } }],
};

// A project the user should see anywhere in Ad Studio: not deleted, not
// hidden (the chat header's "Hide chat" action), and user-facing. Hiding a
// chat removes it AND its ads from every surface except the sidebar's
// "Hidden chats" section and — for chats the user pinned — the "Pinned"
// section, which keeps hidden chats so pinning is never a silent no-op.
// `includeHidden` is the dev-mode escape hatch (the grids' show-hidden-ads
// toggle) — never pass it on customer-facing surfaces.
export function visibleAdStudioProjectFilter(
  includeHidden = false,
): AdStudioProjectsBoolExp {
  return {
    deleted_at: { _is_null: true },
    ...(includeHidden ? {} : { is_hidden: { _eq: false } }),
    ...USER_FACING_AD_STUDIO_PROJECT_FILTER,
  };
}

export const VISIBLE_AD_STUDIO_PROJECT_FILTER: AdStudioProjectsBoolExp =
  visibleAdStudioProjectFilter();

// A brief whose ads belong in the ad grids: not deleted and attached to a
// visible project.
export function visibleAdBriefFilter(includeHidden = false): AdBriefsBoolExp {
  return {
    deleted_at: { _is_null: true },
    ad_studio_project_id: { _is_null: false },
    ad_studio_project: visibleAdStudioProjectFilter(includeHidden),
  };
}

export const VISIBLE_AD_BRIEF_FILTER: AdBriefsBoolExp = visibleAdBriefFilter();

// Hide mirrored sizes in discovery while legacy reads remain authoritative.
export const PRIMARY_AD_BRIEF_ASSET_FILTER: AdBriefAssetsBoolExp = {
  source_ad_brief_asset_version_id: { _is_null: true },
};

// An ad asset that belongs in the ad grids (All ads, folders, favorites) and
// their sidebar counts. The AdFolders query receives this as its $assetWhere
// variable so the counts can never drift from what the grids render.
//
// A NULL `version_id` is a pending ad: `init_ad` mints the brief and asset rows
// before the agent has any content to save, so the row exists with an empty
// `asset_url` and no document. Those render as a "Failed to load" tile and
// inflate every count here, so they stay out until the first save lands.
export function visibleAdBriefAssetFilter(
  includeHidden = false,
): AdBriefAssetsBoolExp {
  return {
    ...PRIMARY_AD_BRIEF_ASSET_FILTER,
    deleted_at: { _is_null: true },
    version_id: { _is_null: false },
    ad_brief: visibleAdBriefFilter(includeHidden),
  };
}

export const VISIBLE_AD_BRIEF_ASSET_FILTER: AdBriefAssetsBoolExp =
  visibleAdBriefAssetFilter();
